import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chip, Button } from '@material-tailwind/react';
import { Edit, Eye, Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { DeleteButton, EditButton, PrimaryButton } from '../Buttons';
import usePrivilege from '../../hooks/usePrivilege';
import { useUser } from '../../context/UserContext';
import { normalizeProjectPhasePayload } from '../../utils/common';
import PhaseModal from './PhaseModal';
import DeveloperProjectBoqTable from '../DeveloperProjectBoqTable';

function PhasesList({ projectPhases, projectId, onPhaseUpdate }) {
  const navigate = useNavigate();
  const { hasPrivilege } = usePrivilege();
  const { isReadOnly, userInfo } = useUser();
  const [isPhaseModalOpen, setIsPhaseModalOpen] = useState(false);
  const [editingPhase, setEditingPhase] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [localPhases, setLocalPhases] = useState([]);
  const [expandedBoqPhaseIds, setExpandedBoqPhaseIds] = useState(new Set());
  const persona = userInfo?.persona || '';
  const showBoqForRole = persona === 'STAFF' || persona === 'DEVELOPER_ADMIN' || persona === 'ZUMALE_USER';

  const getPhaseStatusText = (status) => {
    switch (status) {
      case 1:
        return 'Active';
      case 2:
        return 'Completed';
      case 3:
        return 'On Hold';
      case 4:
        return 'Cancelled';
      default:
        return '';
    }
  };

  const getPhaseStatusColor = (status) => {
    switch (status) {
      case 1:
        return 'green';
      case 2:
        return 'blue';
      case 3:
        return 'yellow';
      case 4:
        return 'red';
      default:
        return 'gray';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleAddPhase = () => {
    setEditingPhase(null);
    setIsPhaseModalOpen(true);
  };

  const handleEditPhase = (phase) => {
    setEditingPhase(phase);
    setIsPhaseModalOpen(true);
  };

  const handleViewPhase = (phase) => {
    const pid = phase.projectPhaseId ?? phase.project_phase_id;
    if (pid == null) return;
    navigate(`/project-details/${projectId}/phase/${pid}`);
  };

  const handlePhaseModalClose = () => {
    setIsPhaseModalOpen(false);
    setEditingPhase(null);
  };

  const handlePhaseSuccess = (phaseData) => {
    const normalized = normalizeProjectPhasePayload(phaseData);
    if (editingPhase) {
      setLocalPhases((prevPhases) =>
        prevPhases.map((phase) =>
          String(phase.projectPhaseId ?? phase.project_phase_id) ===
          String(editingPhase.projectPhaseId ?? editingPhase.project_phase_id)
            ? { ...phase, ...normalized }
            : phase
        )
      );
    } else {
      setLocalPhases((prevPhases) => [...prevPhases, normalized]);
    }

    if (onPhaseUpdate) {
      onPhaseUpdate(normalized);
    }
  };

  // Set loading to false when projectPhases changes and sync local state
  useEffect(() => {
    if (projectPhases !== undefined) {
      setIsLoading(false);
      setLocalPhases(projectPhases);
    }
  }, [projectPhases]);

  return (
    <>
      <div className="border rounded-lg bg-white shadow-md p-4 lg:p-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-xl font-bold">Project Phases</h1>
          {hasPrivilege("canEdit") && (
            <PrimaryButton
              icon={<Plus className="mr-2 size-4" />}
              left
              disabled={isReadOnly}
              onClick={handleAddPhase}
              className={
                isReadOnly || !hasPrivilege("canEdit")
                  ? "cursor-not-allowed opacity-50"
                  : ""
              }
            >
              Add Phase
            </PrimaryButton>
          )}
        </div>

        <div className="max-h-[500px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-50" style={{ height: '500px' }}>
          {isLoading ? (
            // Skeleton Loading States
            <div className="space-y-4">
              {[1, 2, 3].map((index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-xl p-5 animate-pulse">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      {/* Phase Header Skeleton */}
                      <div className="flex items-center gap-3 mb-4">
                        <div className="h-6 bg-gray-200 rounded w-32"></div>
                        <div className="h-5 bg-gray-200 rounded-full w-16"></div>
                      </div>
                      
                      {/* Info Grid Skeleton */}
                      <div className="grid grid-cols-2 gap-6">
                        <div className="space-y-3">
                          <div>
                            <div className="h-3 bg-gray-200 rounded w-12 mb-2"></div>
                            <div className="h-4 bg-gray-200 rounded w-8"></div>
                          </div>
                          <div>
                            <div className="h-3 bg-gray-200 rounded w-12 mb-2"></div>
                            <div className="h-4 bg-gray-200 rounded w-8"></div>
                          </div>
                        </div>
                        
                        <div className="space-y-3">
                          <div>
                            <div className="h-3 bg-gray-200 rounded w-12 mb-2"></div>
                            <div className="h-4 bg-gray-200 rounded w-20"></div>
                          </div>
                          <div>
                            <div className="h-3 bg-gray-200 rounded w-16 mb-2"></div>
                            <div className="h-4 bg-gray-200 rounded w-24"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Action Buttons Skeleton */}
                    <div className="flex gap-2 ml-4">
                      <div className="h-8 w-8 bg-gray-200 rounded"></div>
                      <div className="h-8 w-8 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : localPhases && localPhases.length > 0 ? (
            localPhases
              .filter((phase) => phase && (phase.projectPhaseId ?? phase.project_phase_id))
              .map((phase) => (
              <div
                key={phase.projectPhaseId}
                className="bg-white border border-gray-200 rounded-xl p-5 mb-4 hover:shadow-md transition-all duration-200 hover:border-gray-200"
              >
                <div className="flex justify-between items-start">
                  {/* Phase Header */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-4">
                      <h3 className="text-lg font-medium text-gray-900">{phase?.name}</h3>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        phase?.phaseStatus === 1 ? 'bg-green-50 text-green-700' :
                        phase?.phaseStatus === 2 ? 'bg-blue-50 text-blue-700' :
                        phase?.phaseStatus === 3 ? 'bg-yellow-50 text-yellow-700' :
                        phase?.phaseStatus === 4 ? 'bg-red-50 text-red-700' :
                        'bg-gray-50 text-gray-700'
                      }`}>
                        {getPhaseStatusText(phase?.phaseStatus)}
                      </span>
                    </div>

                    {/* Essential Info Grid */}
                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                            Towers
                          </p>
                          <p className="text-sm text-gray-700">{phase?.towers || 0}</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                            Units
                          </p>
                          <p className="text-sm text-gray-700">{phase?.units || 0}</p>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        {phase?.reraNo && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                              RERA
                            </p>
                            <p className="text-sm text-gray-700">{phase.reraNo}</p>
                          </div>
                        )}
                        {phase?.reraStartDate && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                              Start Date
                            </p>
                            <p className="text-sm text-gray-700">{formatDate(phase.reraStartDate)}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  {(hasPrivilege("canView") || hasPrivilege("canEdit")) && (
                    <div className="flex gap-2 ml-4">
                      <Button
                        size="sm"
                        variant="outline"
                        color="gray"
                        className="p-2 hover:bg-gray-50 border-gray-200"
                        onClick={() => handleViewPhase(phase)}
                      >
                        <Eye size={16} />
                      </Button>
                      {hasPrivilege("canEdit") && (
                        <Button
                          size="sm"
                          variant="outline"
                          color="gray"
                          className="p-2 hover:bg-gray-50 border-gray-200"
                          onClick={() => handleEditPhase(phase)}
                          disabled={isReadOnly}
                        >
                          <Edit size={16} />
                        </Button>
                      )}
                    </div>
                  )}
                </div>

              
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-300 mb-4">
                <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <p className="text-gray-600 font-medium text-lg mb-2">No phases added yet</p>
              {hasPrivilege("canEdit") && (
                <p className="text-gray-400 text-sm">Click "Add Phase" to create the first phase</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Phase Modal */}
      <PhaseModal
        isOpen={isPhaseModalOpen}
        onClose={handlePhaseModalClose}
        phase={editingPhase}
        projectId={projectId}
        onSuccess={handlePhaseSuccess}
      />
    </>
  );
}

export default PhasesList;